// Color utility functions for consistent color schemes across the app

// Resolved once per class: each miss costs a style recalc, and these are read inside chart option builders
const swatchCache = new Map<string, string>();

/** A Tailwind class's resolved colour for canvas APIs (Chart.js, mapbox); pass the whole class literal or Tailwind purges it. */
export const readSwatch = (textClass: string): string => {
  const cached = swatchCache.get(textClass);
  if (cached !== undefined) return cached;
  // Non-browser callers get "" rather than a hardcoded fallback, which would be a second source of truth
  if (typeof document === "undefined") return "";

  const probe = document.createElement("span");
  probe.className = textClass;
  probe.style.display = "none";
  document.body.appendChild(probe);
  const value = getComputedStyle(probe).color;
  probe.remove();

  swatchCache.set(textClass, value);
  return value;
};

/**
 * Converts hex color to RGB
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
};

/**
 * Converts RGB to hex color
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

/**
 * Generates a linear interpolation between two colors
 * @param startColor - Starting color in hex format
 * @param endColor - Ending color in hex format
 * @param steps - Number of colors to generate
 * @returns Array of interpolated colors in hex format
 */
export const interpolateColors = (
  startColor: string,
  endColor: string,
  steps: number
): string[] => {
  if (steps <= 1) return [startColor];
  if (steps === 2) return [startColor, endColor];

  const start = hexToRgb(startColor);
  const end = hexToRgb(endColor);
  const colors: string[] = [];

  for (let i = 0; i < steps; i++) {
    const ratio = i / (steps - 1);
    const r = Math.round(start.r + (end.r - start.r) * ratio);
    const g = Math.round(start.g + (end.g - start.g) * ratio);
    const b = Math.round(start.b + (end.b - start.b) * ratio);
    colors.push(rgbToHex(r, g, b));
  }

  return colors;
};

/**
 * Generates percentile colors for forecast graphs
 * Uses brand green colors from light to dark
 * @param numColors - Number of colors to generate
 * @returns Array of colors suitable for percentile bands
 */
export const generatePercentileColors = (numColors: number): string[] => {
  const lightGreen = "#cfd5b3"; // Light green from brand
  const darkGreen = "#879643"; // Dark green from brand
  
  return interpolateColors(lightGreen, darkGreen, numColors);
};

/**
 * Generates percentile colors with opacity for filled bands
 * @param numBands - Number of bands to generate
 * @returns Array of rgba colors with increasing opacity
 */
export const generatePercentileBandColors = (numBands: number): string[] => {
  const baseGreen = "#879643"; // Dark green from brand
  const rgb = hexToRgb(baseGreen);
  const colors: string[] = [];

  for (let i = 0; i < numBands; i++) {
    // Start with lightest opacity, increase for inner bands
    const opacity = 0.15 + (i * 0.25 / Math.max(1, numBands - 1));
    colors.push(`rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`);
  }

  return colors;
};

/**
 * Brand percentile colors for backward compatibility
 * These match the existing color scheme
 */
export const PERCENTILE_COLORS = [
  "#cfd5b3",
  "#abb57b",
  "#879643",
  "#abb57b",
  "#cfd5b3",
];

/**
 * Gets percentile color for a given index
 * Uses modulo to handle any number of percentiles
 */
export const getPercentileColor = (index: number, total: number): string => {
  const colors = generatePercentileColors(total);
  return colors[index] || PERCENTILE_COLORS[index % PERCENTILE_COLORS.length];
};

/**
 * Distinct colors for overlay lines on combined-source / multi-series charts.
 */
export const OVERLAY_COLORS = [
  "#2563eb", // blue
  "#dc2626", // red
  "#16a34a", // green
  "#9333ea", // purple
  "#ea580c", // orange
  "#0891b2", // cyan
  "#be185d", // pink
  "#4f46e5", // indigo
];

// Custom View sources other than the model, in fixed order — blue, orange, purple, cyan, then green, pink, indigo. No red (it's the danger colour), and blue never sits next to purple (they merge for deuteranopes); validated for colour-blind separation and contrast in light and dark
export const CUSTOM_VIEW_SOURCE_COLORS = [0, 4, 3, 5, 2, 6, 7].map((i) => OVERLAY_COLORS[i]);

/**
 * Chart colormap for non-percentile data series
 */
export const CHART_COLORS = [
  "#16537e", // Primary blue
  "#FF5733", // Orange-red  
  "#3357FF", // Blue
  "#FF33A1", // Pink
  "#A133FF", // Purple
  "#33FFF1", // Cyan
  "#FFC300", // Gold
  "#DAF7A6", // Light green
  "#C70039", // Dark red
  "#4B0082", // Indigo
  "#008080", // Teal
  "#FF6347", // Tomato
  "#4682B4", // Steel blue
  "#32CD32", // Lime green
  "#FF1493", // Deep pink
  "#00CED1", // Dark turquoise
  "#FF8C00", // Dark orange
  "#9370DB", // Medium purple
  "#00FA9A", // Medium spring green
  "#DC143C", // Crimson
];